// frontend/src/samkalp-admin/tabs/ProjectAccessTab.js
//
// Duplicated from frontend/src/pages/AskOSAdmin.js's ProjectAccessTab (not
// exported there). One addition over the original: projects inherited from
// a parent tenant (source === "inherited") get a small badge, since this
// dashboard is the first place that distinction is user-visible.
import React, { useState, useEffect } from 'react';
import { Card, Table, Row, Col, Button, Alert, Spinner } from 'react-bootstrap';
import { Users, BookOpen, ToggleLeft, ToggleRight } from 'lucide-react';
import api from '../../api/api';
import { useAlert } from './shared';

const ProjectAccessTab = ({ slug }) => {
    const [students, setStudents] = useState([]);
    const [projects, setProjects] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [selected, setSelected] = useState(null);
    const [alert, showAlert]      = useAlert();

    useEffect(() => {
        const load = async () => {
            try {
                const [s, p, m] = await Promise.all([
                    api.get(`/tenant/${slug}/askos/admin/students`),
                    api.get(`/tenant/${slug}/askos/admin/projects`),
                    api.get(`/tenant/${slug}/askos/admin/student-project-mappings`),
                ]);
                setStudents(s.data); setProjects(p.data); setMappings(m.data);
            } catch { /* empty */ }
            setLoading(false);
        };
        load();
    }, [slug]);

    const hasAccess = (studentId, projectId) =>
        mappings.some(m => m.student_id === studentId && m.project_id === projectId);

    const toggle = async (studentId, projectId, currently) => {
        try {
            if (currently) {
                await api.delete(`/tenant/${slug}/askos/admin/student-project-mappings`,
                    { data: { student_id: studentId, project_id: projectId } });
                setMappings(prev => prev.filter(m => !(m.student_id === studentId && m.project_id === projectId)));
                showAlert('Access removed.', 'warning');
            } else {
                await api.post(`/tenant/${slug}/askos/admin/student-project-mappings`,
                    { student_id: studentId, project_id: projectId });
                setMappings(prev => [...prev, { student_id: studentId, project_id: projectId }]);
                showAlert('Access granted.');
            }
        } catch (e) { showAlert(e.response?.data?.detail || 'Action failed.', 'danger'); }
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border" size="sm" aria-hidden="true" /></div>;

    return (
        <>
            {alert && <Alert variant={alert.variant} className="py-2 small mb-3" role="status">{alert.msg}</Alert>}
            <p className="text-muted small mb-4">
                Select a student to manage which subject projects they can access in AskOS. Toggles take effect immediately.
            </p>
            <Row className="g-3">
                <Col md={4}>
                    <Card className="faculty-card h-100">
                        <Card.Header className="bg-transparent border-0 pb-0 pt-3 px-3">
                            <p className="x-small fw-bold text-muted text-uppercase mb-0">Students</p>
                        </Card.Header>
                        <Card.Body className="p-2">
                            {students.length === 0
                                ? <p className="x-small text-muted text-center py-4">No students added yet</p>
                                : students.map(s => (
                                    <button key={s.id}
                                        className={`d-flex align-items-center gap-3 w-100 border-0 rounded-3 px-3 py-2 mb-1 text-start transition ${selected?.id === s.id ? 'bg-primary text-white' : 'bg-transparent text-dark'}`}
                                        style={{ cursor: 'pointer' }} onClick={() => setSelected(s)}>
                                        <div className={`rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0 ${selected?.id === s.id ? 'bg-white text-primary' : 'bg-primary bg-opacity-10 text-primary'}`}
                                            style={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                                            {(s.full_name || s.email)[0].toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="small fw-semibold text-truncate">{s.full_name || s.email}</div>
                                            <div className={`x-small text-truncate ${selected?.id === s.id ? 'opacity-75' : 'text-muted'}`}>
                                                {mappings.filter(m => m.student_id === s.id).length} subjects
                                            </div>
                                        </div>
                                    </button>
                                ))
                            }
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={8}>
                    <Card className="faculty-card h-100">
                        <Card.Header className="bg-transparent border-0 pb-0 pt-3 px-4">
                            <p className="x-small fw-bold text-muted text-uppercase mb-0">
                                {selected ? `Project access — ${selected.full_name || selected.email}` : 'Select a student'}
                            </p>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {!selected ? (
                                <div className="text-center py-5 text-muted"><Users size={28} className="mb-2 opacity-50" /><p className="small">Choose a student from the left</p></div>
                            ) : projects.length === 0 ? (
                                <div className="text-center py-5 text-muted"><BookOpen size={28} className="mb-2 opacity-50" /><p className="small">No approved projects available yet</p></div>
                            ) : (
                                <Table hover className="mb-0 small">
                                    <tbody>
                                        {projects.map(p => {
                                            const active = hasAccess(selected.id, p.id);
                                            return (
                                                <tr key={p.id}>
                                                    <td className="py-3 px-4">
                                                        <div className="fw-semibold text-dark d-flex align-items-center gap-2">
                                                            {p.name}
                                                            {p.source === 'inherited' && (
                                                                <span className="badge bg-light text-dark border x-small fw-normal">From Samkalp</span>
                                                            )}
                                                        </div>
                                                        <div className="x-small text-muted">Project #{p.id}</div>
                                                    </td>
                                                    <td className="py-3 px-4 text-end align-middle">
                                                        <Button variant={active ? 'primary' : 'outline-secondary'} size="sm"
                                                            className="rounded-pill d-inline-flex align-items-center gap-2"
                                                            onClick={() => toggle(selected.id, p.id, active)}>
                                                            {active ? <><ToggleRight size={14} /> Access On</> : <><ToggleLeft size={14} /> Access Off</>}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default ProjectAccessTab;
